import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { getDayInResourceTz, getResourceTimezoneLabel } from '../../utils/datetime';
import { getChartTooltipStyle } from '../../utils/chartDefaults';
import { downloadCsv, toCsv } from '../../utils/csv';
import { EXCLUDED_HATCH_CLASS, withExcludedEntries } from '../../utils/excludedDays';
import excludedDaysPlugin from './excludedDaysPlugin';

const TZ_LABEL = getResourceTimezoneLabel();
const NO_EXCLUDED_DAYS = new Map();

// This chart mixes bar datasets with `type: 'line'` threshold overlays.
// react-chartjs-2's <Bar> only auto-registers BarController, so we must
// register LineController (and friends) ourselves — otherwise a direct
// load of a line-bearing route throws "line is not a registered controller".
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

// Mirror Reports.js: align Chart.js text with the app's body font stack so
// chart axis numbers render in SF Pro (system stack) instead of Chart.js's
// default Helvetica Neue. Setting this here too covers routes that mount
// DailyScoreChart without going through Reports.js first.
// Chart.js font family is configured globally in src/utils/chartDefaults.js.

const isDark = () =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark');

// Bar fills (penalty days vs normal days). Penalty days were over the
// per-day error allowance and count differently in monthly aggregation.
const PENALTY_FILL = 'rgba(239, 68, 68, 0.85)';
const PENALTY_BORDER = 'rgba(239, 68, 68, 1)';
// Match the forecast-interval band used in ForecastComparisonChart /
// ChartUplot: light = mid-gray (169,169,169), dark = slate-300
// (203,213,225). Bars need a higher alpha than overlay bands so daily
// values stay legible against the surface.
const NORMAL_FILL_LIGHT = 'rgba(169, 169, 169, 0.45)';
const NORMAL_BORDER_LIGHT = 'rgba(153, 153, 153, 1)';
const NORMAL_FILL_DARK = 'rgba(203, 213, 225, 0.35)';
const NORMAL_BORDER_DARK = 'rgba(203, 213, 225, 0.75)';

// League threshold line colors — mirror the tier palette in `rewards.md`
// (Elite brand teal / Challenger slate-500 / Runner-Up slate-400).
const TIER_LINES = [
  { key: 'elite', label: 'Elite Threshold', color: 'rgba(5, 120, 138, 0.95)' },
  { key: 'challenger', label: 'Challenger Threshold', color: 'rgba(71, 85, 105, 0.95)' },
  { key: 'runner_up', label: 'Runner-Up Threshold', color: 'rgba(148, 163, 184, 0.95)' },
];

// Build a date → value lookup for one threshold series so we can align
// the line points to the same date axis as the bars (and leave gaps when
// the threshold isn't published for a given day). Null values are skipped
// — the engine emits null when the qualified pool is too small to anchor
// that tier (e.g. challenger needs ≥10, runner_up needs ≥11), and
// Number(null) === 0 would otherwise drop the line to the x-axis.
function indexThresholdSeries(series) {
  if (!Array.isArray(series)) return new Map();
  const map = new Map();
  for (const item of series) {
    if (!item || !item.target_day || item.value == null) continue;
    map.set(item.target_day, item.value);
  }
  return map;
}

// CSV download of the plotted days: one row per day, with the day's
// thresholds. An excluded day carries only its reason, as it has no bar.
function dailyScoresCsv(days, thresholds) {
  const tiers = TIER_LINES.map((tier) => indexThresholdSeries(thresholds?.[tier.key]));
  return toCsv([
    [
      'date', 'score', 'penalty', 'excluded_reason',
      ...TIER_LINES.map((tier) => `${tier.key}_threshold`),
    ],
    ...days.map((d) => (d.excludedReason
      ? [d.date, null, null, d.excludedReason, ...tiers.map(() => null)]
      : [d.date, d.value, Boolean(d.isPenalty), null, ...tiers.map((idx) => idx.get(d.date))])),
  ]);
}

/**
 * Bar chart of daily errors with optional league-threshold overlay. Bars
 * are gray for normal days and red for days that hit the per-day penalty.
 * Three dashed lines (Elite / Challenger / Runner-Up) trace the cohort's
 * tier thresholds over the same dates so the forecaster can read off
 * where each day landed at a glance.
 *
 * @param {{
 *   dailyScores: Array<{ date: string, value: number | null, isPenalty?: boolean }>,
 *   thresholds?: { elite?: Array<{target_day: string, value: number}>, challenger?: Array<{target_day: string, value: number}>, runner_up?: Array<{target_day: string, value: number}> } | null,
 *   metricLabel?: string,
 *   onDayClick?: (dateStr: string) => void,
 *   dayClickDisabledHint?: string,
 *   monthLabel?: string,
 *   className?: string,
 *   excludedDays?: Map<string, string>,
 *   exportFileName?: string,
 * }} props
 *
 * `excludedDays` (ISO day -> reason) are outage days left out of rankings and
 * payments: they stay on the axis with no bar, under a hatch.
 *
 * With `exportFileName`, a button in the card's top-right corner downloads
 * the plotted days as that CSV file.
 */
export default function DailyScoreChart({
  dailyScores = [],
  thresholds = null,
  metricLabel = 'Score',
  onDayClick,
  dayClickDisabledHint,
  monthLabel,
  className = '',
  // Fill the parent's height/width instead of the fixed 320px x min-480px
  // scroll box. Used by the phone full-screen view, where the canvas's
  // touch-action:none makes an overflow container unscrollable by touch.
  fill = false,
  excludedDays = NO_EXCLUDED_DAYS,
  exportFileName,
}) {
  const dark = isDark();
  const textColor = dark ? '#e5e7eb' : '#374151';
  const gridColor = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
  const normalFill = dark ? NORMAL_FILL_DARK : NORMAL_FILL_LIGHT;
  const normalBorder = dark ? NORMAL_BORDER_DARK : NORMAL_BORDER_LIGHT;

  const days = useMemo(
    () => withExcludedEntries(dailyScores, excludedDays),
    [dailyScores, excludedDays],
  );
  const excludedIdx = useMemo(
    () => days.flatMap((d, i) => (d.excludedReason ? [i] : [])),
    [days],
  );

  const data = useMemo(() => {
    const labels = days.map((d) => {
      const day = getDayInResourceTz(d.date);
      return day == null ? '' : String(day);
    });

    const barColors = days.map((d) => (d.isPenalty ? PENALTY_FILL : normalFill));
    const barBorders = days.map((d) => (d.isPenalty ? PENALTY_BORDER : normalBorder));

    const datasets = [
      {
        type: 'bar',
        label: metricLabel,
        data: days.map((d) => (d.value == null ? null : Number(d.value))),
        backgroundColor: barColors,
        borderColor: barBorders,
        borderWidth: 1,
        borderRadius: 4,
        maxBarThickness: 28,
        order: 2,
      },
    ];

    // Threshold lines: each point is looked up by the day's date so they
    // align with the bars instead of relying on a separate sort order.
    if (thresholds) {
      for (const tier of TIER_LINES) {
        const idx = indexThresholdSeries(thresholds[tier.key]);
        if (idx.size === 0) continue;
        datasets.push({
          type: 'line',
          label: tier.label,
          data: days.map((d) => (idx.has(d.date) ? Number(idx.get(d.date)) : null)),
          borderColor: tier.color,
          backgroundColor: tier.color,
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 3,
          pointHoverRadius: 5,
          spanGaps: true,
          fill: false,
          tension: 0.1,
          order: 1,
        });
      }
    }

    return { labels, datasets };
  }, [days, thresholds, metricLabel, normalFill, normalBorder]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_evt, elements) => {
        if (!onDayClick || !elements || !elements.length) return;
        // Only react to bar clicks; ignore clicks on the threshold lines.
        const barEl = elements.find((el) => el.datasetIndex === 0);
        const idx = barEl ? barEl.index : null;
        if (idx == null) return;
        const dateStr = days[idx]?.date;
        if (dateStr) onDayClick(dateStr);
      },
      onHover: (_evt, elements, chart) => {
        if (!chart || !chart.canvas) return;
        const overBar = onDayClick && elements && elements.some((el) => el.datasetIndex === 0);
        chart.canvas.style.cursor = overBar ? 'pointer' : 'default';
      },
      plugins: {
        excludedDays: { indices: excludedIdx, dark },
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: textColor,
            usePointStyle: true,
            padding: 14,
            boxWidth: 10,
            boxHeight: 10,
            font: { size: 11 },
          },
        },
        tooltip: {
          ...getChartTooltipStyle(dark),
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (v == null) return `${ctx.dataset.label}: —`;
              const formatted = Number(v).toFixed(2);
              if (ctx.dataset.type === 'bar') {
                const d = days[ctx.dataIndex];
                const penaltyTag = d?.isPenalty ? ' (PENALTY)' : '';
                const base = `${metricLabel}: ${formatted}${penaltyTag}`;
                if (onDayClick) return [base, 'Click for drill-down'];
                if (dayClickDisabledHint) return [base, dayClickDisabledHint];
                return base;
              }
              return `${ctx.dataset.label}: ${formatted}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: textColor, maxRotation: 0, autoSkip: true },
        },
        y: {
          beginAtZero: true,
          grid: { color: gridColor },
          ticks: { color: textColor },
          title: {
            display: true,
            text: `${metricLabel} score`,
            color: textColor,
            font: { size: 11 },
          },
        },
      },
    }),
    [textColor, gridColor, dark, onDayClick, days, metricLabel, dayClickDisabledHint, excludedIdx],
  );

  // A tier is "present" only if its series carries at least one non-null
  // value — a series of all-nulls means the engine never had enough
  // qualified peers to anchor that cutoff over the period. We use this to
  // hide the legend swatch for missing tiers and to explain WHY in the
  // footnote, instead of silently dropping the line.
  const presentTiers = thresholds
    ? TIER_LINES.filter((t) => {
        const series = thresholds[t.key];
        return (
          Array.isArray(series) &&
          series.some((it) => it && it.value != null)
        );
      })
    : [];
  const missingTiers = thresholds
    ? TIER_LINES.filter((t) => !presentTiers.includes(t))
    : [];
  const hasThresholds = presentTiers.length > 0;
  const hasPenalty = days.some((d) => d.isPenalty);
  const hasExcluded = excludedIdx.length > 0;

  const missingTierNote = (() => {
    if (missingTiers.length === 0) return null;
    const names = missingTiers
      .map((t) => t.label.replace(' Threshold', ''))
      .join(' & ');
    const verb = missingTiers.length > 1 ? "haven't" : "hasn't";
    return `${names} threshold${missingTiers.length > 1 ? 's' : ''} ${verb} formed yet — too few qualified peers this period.`;
  })();

  return (
    <section className={`${fill ? 'h-full flex flex-col' : ''} ${className}`.trim()}>
      <header className={fill ? 'mb-2 landscape:hidden' : 'mb-2'}>
        <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
          Daily {String(metricLabel).toUpperCase()}
        </div>
        <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
          {monthLabel ? `Your daily errors for ${monthLabel}` : 'Your daily errors'}
        </h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          One bar per day ({TZ_LABEL} days). Lower is better.
        </p>
      </header>
      <div
        className={`relative rounded-xl bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-sm ${
          fill ? 'p-3 flex-1 min-h-0 flex flex-col' : 'p-5'
        }`}
      >
        {exportFileName ? (
          <button
            type="button"
            onClick={() => downloadCsv(exportFileName, dailyScoresCsv(days, thresholds))}
            aria-label="Download daily scores (CSV)"
            title="Download daily scores (CSV)"
            className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-lg border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 px-2 py-1 text-xs font-semibold text-brand-600 dark:text-brand-400 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">CSV</span>
          </button>
        ) : null}
        <div className={fill ? 'flex-1 min-h-0' : 'overflow-x-auto'}>
          <div style={fill ? { height: '100%' } : { height: 320, minWidth: 480 }}>
            <Bar data={data} options={options} plugins={[excludedDaysPlugin]} />
          </div>
        </div>
        {(hasPenalty || hasThresholds || missingTierNote || hasExcluded) ? (
          <div className="mt-3 space-y-1">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
              {hasPenalty ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: PENALTY_FILL }} />
                  Red bars indicate days with penalties.
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: normalFill, border: '1px solid ' + normalBorder }} />
                Gray bars indicate normal submissions.
              </span>
              {hasThresholds ? (
                <span>Dashed lines trace the daily league thresholds.</span>
              ) : null}
              {hasExcluded ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className={`inline-block h-2.5 w-2.5 rounded-sm ${EXCLUDED_HATCH_CLASS}`} />
                  Excluded day, left out of rankings and payments.
                </span>
              ) : null}
            </div>
            {missingTierNote ? (
              <p className="text-[11px] italic text-gray-500 dark:text-gray-400">
                {missingTierNote}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
